/*!
Copyright 2012-2026 Sarven Capadisli <https://csarven.ca/>
Copyright 2023-2026 Virginia Balseiro <https://virginiabalseiro.com/>

Licensed under the Apache License, Version 2.0 (the "License");
you may not use this file except in compliance with the License.
You may obtain a copy of the License at

    http://www.apache.org/licenses/LICENSE-2.0

Unless required by applicable law or agreed to in writing, software
distributed under the License on an "AS IS" BASIS,
WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
See the License for the specific language governing permissions and
limitations under the License.
*/

import { getSelectedParentElement, restoreSelection, getInboxOfClosestNodeWithSelector, createNoteData} from "../../utils/annotation.js";
import { generateAttributeId, getDateTimeISO } from "../../../util.js"
import { getNodeLanguage, getFormValues, createHTML } from "../../../utils/html.js";
import { getReferenceLabel, createActivityHTML, createNoteDataHTML, getRegisteredAnnotationContainer } from "../../../doc.js";
import { getAbsoluteIRI, stripFragmentFromString } from "../../../uri.js"
import Config from "../../../config.js"
import { notifyInbox, postActivity, showActivities, registerAnnotationInTypeIndex, markAnnotationTarget } from "../../../activity.js"
import { shareResource } from "../../../dialog.js";
import { domSanitize } from "../../../utils/sanitization.js";
import { encryptContent } from "../../../crypto.js";
import { isUnlocked, getSessionPublicKey, getSessionKid } from "../../../keystore.js";

const ns = Config.ns;

//select text
//open popup
//click toolbar button
//populate form
//fill out form
//submit form

//validate form
//post to external location(s)
//copy to storage
//mark the highlight text
//add the note as an aside
//update message log
//do other things...

// TODO: refactor to generalize listeners on form
// addListeners([type, callback]) {
// [listeners] => addlistener(type, callback)}
// callback { updateUI, sendfetch}

//actions = ['approve', 'disapprove', 'specificity', 'bookmark', 'comment', 'note']

//actions = ['approve', 'disapprove', 'specificity'] //Review
//actions = ['selector', 'approve', 'disapprove', 'specificity', 'bookmark', 'comment'] //Social
//actions = ['note'] //Author

export function shareButtonHandler(e) {
  const selector = this.getTextQuoteSelector();
  const baseURL = stripFragmentFromString(window.location.href);
  const uri = getAnnotationSelectorStateURI(baseURL, selector);

  shareResource(e, uri);
}

export function formHandlerAnnotate(e, action) {
  e.preventDefault();
  e.stopPropagation();

  restoreSelection(this.selection);
  const selection = window.getSelection();

  const range = selection.getRangeAt(0);
  const selectedParentElement = getSelectedParentElement(range);

  const formValues = getFormValues(e.target);

  //TODO: Mark the selection after successful comment. Move out.
  //TODO: Use node.textBetween to determine prefix, exact, suffix + parentnode with closest id
  //Mark the selected content in the document
  const selector = this.getTextQuoteSelector();

  const selectionData = {
    selection,
    selector,
    selectedParentElement,
    selectedContent: this.getSelectionAsHTML()
  };

  const annotationInboxLocation = formValues[`${action}-annotation-inbox`];
  const annotationLocationAnnotationStore = formValues[`${action}-annotation-location-annotation-store`];
  const annotationLocationPersonalStorage = formValues[`${action}-annotation-location-personal-storage`];
  const annotationLocationActivityOutbox = formValues[`${action}-annotation-location-activity-outbox`];
  const annotationLocationService = formValues[`${action}-annotation-location-annotation-service`];

  updateUserUI({ annotationInboxLocation, annotationLocationAnnotationStore, annotationLocationPersonalStorage, annotationLocationActivityOutbox, annotationLocationService })

  processAction(action, formValues, selectionData);

  this.cleanupToolbar();
}


function updateUserUI(fields) {
  Object.entries(fields).forEach(([key, value]) => {
    Config.User.UI[key] = { checked: Boolean(value) };
  });
}


export async function processAction(action, formValues, selectionData) {
  //TODO:

  const data = getFormActionData(action, formValues, selectionData);

  // Sanitize the user-supplied note body here, at the boundary. The generated
  // annotation markup (lib RDFa + createHTML wrapper) is trusted and is NOT
  // sanitized as a whole - only this user input is.
  if (data.formData && typeof data.formData.content === 'string') {
    data.formData.content = domSanitize(data.formData.content);
  }

  const { annotationDistribution, ...otherFormData } = data;

  let noteHTML, note;

  //XXX: Sort of a placeholder switch but don't really need it now
  switch(action) {
    case 'approve': case 'disapprove': case 'specificity': case 'bookmark': case 'comment':
      for (const annotationData of annotationDistribution) {
        // We want to have one object with the annotation distribution data and remaining data from the form that is the same for all annotations. However, we need to overwrite any default values that we got initially from the form data with the latest calculations from annotation distribution data.
        const annotation = {
          ...otherFormData,
          ...annotationData
        };

        var noteData = createNoteData(annotation);

        // POST with a relative @id ('') so the stored annotation is addressed by its
        // storage URL (the server resolves '' against it), not the urn:uuid. The urn
        // stays as oa:canonical. This matches dokieli's long-standing RDFa form and
        // keeps the mark/panel/delete references (all URL-based) consistent.
        noteData.iri = '';

        annotation['motivatedBy'] = noteData['motivatedBy'];

        if (formValues[`${action}-encrypt`] === 'true' && isUnlocked()) {
          const pubKey = getSessionPublicKey();
          const kid = getSessionKid();
          const enc = async v => v ? encryptContent(v, [pubKey], kid) : v;

          if (noteData.body) {
            noteData.body = await Promise.all(noteData.body.map(async item => {
              if (item.value && item.purpose !== 'tagging' && item.purpose !== ns.oa.tagging.value) {
                return { ...item, value: await enc(item.value) };
              }
              return item;
            }));
          }

          if (noteData.target?.selector) {
            const sel = noteData.target.selector;
            [sel.exact, sel.prefix, sel.suffix] = await Promise.all([
              enc(sel.exact), enc(sel.prefix), enc(sel.suffix)
            ]);
          }
        }

        if ('profile' in annotation && annotation.profile == 'https://www.w3.org/ns/activitystreams') {
          var notificationData = createActivityData(annotation, { 'relativeObject': true });
          notificationData['statements'] = createNoteDataHTML(noteData);
          note = createActivityHTML(notificationData);
        }
        else {
          note = createNoteDataHTML(noteData);
        }

        noteHTML = createHTML('', note);

        // console.log(noteData)
        // console.log(data)
        // console.log(annotation)

        // Carry the structured annotation (noteData) so postActivity can
        // serialize JSON-LD directly from it via @dokieli/web-annotation when the
        // server prefers JSON-LD (instead of the RDFa → graph round-trip).
        postActivity(annotation['containerIRI'], annotation.id, noteHTML, { ...annotation, annotationObject: noteData })
          .catch(error => {
            // console.log('Error serializing annotation:', error)
            // console.log(error)
            throw error  // re-throw, break out of promise chain
          })

          .then(response => {
            var location = response.headers.get('Location');

            if (location) {
              location = domSanitize(getAbsoluteIRI(annotation['containerIRI'], location));
              annotation['noteIRI'] = annotation['noteURL'] = location;
            }

            if (annotation.canonical) {
              registerAnnotationInTypeIndex(annotation['containerIRI'], ns.oa.Annotation.value);

              // Mark the passage now, from the in-memory selector - the re-fetch path
              // (positionActivity -> showActivities) 429s under load, so the highlight
              // must not depend on it. Idempotent against the re-fetch path. Uses the
              // library-decided selector (RangeSelector for cross-node selections) so the
              // highlight spans multiple nodes/sections, not just a single TextQuote match.
              markAnnotationTarget(annotation['noteIRI'], noteData.target?.selector || annotation.selectionData?.selector, { motivatedBy: annotation.motivatedBy, id: annotation.id });
            }

            // console.log(annotation, data.options)

            return positionActivity(annotation, data.options);
          })

          .then(() => {
            if (action != 'bookmark') {
              return sendNotification(annotation, data.options);
            }
          })

          .catch(e => {  // catch-all
            // suppress the error, it was already logged to the console above
            // nothing else needs to be done, the loop will proceed
            // to the next annotation
          });
      }
      break;

    // case 'selector':
    //   window.history.replaceState({}, null, selectorIRI);
    
    //   var message = 'Copy URL from address bar.';
    //   message = {
    //     'content': message,
    //     'type': 'info',
    //     'timer': 3000
    //   }
    //   addMessageToLog(message, Config.MessageLog);
    //   showActionMessage(document.documentElement, message);
    //   // TODO: Perhaps use something like setCopyToClipboard instead. Use as `encodeURI(selectorIRI)` as input.
    //   break;
  }
}




//TODO: MOVE

export function getFormActionData(action, formValues, selectionData) {
// console.log(selectionData)
// console.log(selectionData.selectedParentElement)

  const data = {
    action: action,
    selectionData: selectionData,
    id: generateAttributeId(),
    datetime: getDateTimeISO(),
    resourceIRI: Config.DocumentURL,
    containerIRI: window.location.href,
    contentType: 'text/html',
    options: {},
    annotationDistribution: [],
    formData: {}, // keep the forms with modified keys in this object

    parentNodeWithId: selectionData.selectedParentElement?.closest('[id]'),

    //Role/Capability for Authors/Editors
    // ref: '',
    // refType: '', //TODO: reference types. UI needs input
    //TODO: replace refId and noteIRI IRIs

    //This class is added if it is only for display purposes e.g., loading an external annotation for view, but do not want to save it later on (as it will be stripped when 'do' is found)
    // doClass: '',

    //TODO: oa:TimeState's datetime should equal to hasSource value. Same for oa:HttpRequestState's rdfs:value
    // <span about="[this:#' + refId + ']" rel="oa:hasState">(timeState: <time typeof="oa:TimeState" datetime="' + datetime +'" datatype="xsd:dateTime"property="oa:sourceDate">' + datetime + '</time>)</span>\n\

    // noteData: {},
    // note: '',
    // rights: '',
    motivatedBy: Config.ActionToMotivation[action] || 'oa:replying'
  };

  //TODO: Revisit for security or other concerns since this stores any field with pattern `{action}-`
  Object.entries(formValues).forEach(([key, value]) => {
    if (key.startsWith(`${action}-`)) {
      data.formData[key.substring(action.length + 1)] = value;
    }
  });

  //FIXME: Doublecheck if this should be data.formData instead of data.type
  //TODO: If the citation-type is separated into their own actions, we don't need this.
  if (data['type'] == 'ref-footnote') {
    data.motivatedBy = 'oa:describing';
  }
  else if (data['type'] == 'ref-reference') {
    data.motivatedBy = 'oa:linking';
  }

  data.refLabel = getReferenceLabel(data.motivatedBy);

  data.refId = 'r-' + data.id;
  data.targetIRI = (data.parentNodeWithId) ? data.resourceIRI + '#' + data.parentNodeWithId.id : data.resourceIRI;

  data.latestVersion = Config.Resource[data.resourceIRI].graph.out(ns.rel['latest-version']).values[0];

  //XXX: This needs to be revisited. Perhaps showAnnotations is not checking the latestVersion?
  if (data.latestVersion) {
    data.resourceIRI = data.latestVersion;
    data.targetIRI = (data.parentNodeWithId) ? data.latestVersion + '#' + data.parentNodeWithId.id : data.latestVersion;
    data.options.targetInMemento = true;
  }

  // console.log(latestVersion)
  // console.log(resourceIRI)
  // console.log(targetIRI)

  data.targetLanguage = getNodeLanguage(data.parentNodeWithId);
  data.selectionLanguage = getNodeLanguage(data.selectionData.selectedParentElement);
  // console.log(targetLanguage, selectionLanguage)

  //TODO: Revisit this to see whether resourceIRI should be the original or the one that gets updated after latestVersion check.
  data.selectorIRI = getAnnotationSelectorStateURI(data.resourceIRI, data.selectionData.selector);

  data.annotationDistribution = getAnnotationDistribution(action, data);

  return data;
}

//TODO: Generalise this later to handle different selector and states, and parameters ( https://www.w3.org/TR/selectors-states/ )
//Also consider if in extension mode (and current document doesn't have dokieli, https://wicg.github.io/scroll-to-text-fragment/ )
export function getAnnotationSelectorStateURI(baseURL, selector) {
  baseURL = baseURL || window.location.href;
  baseURL = stripFragmentFromString(baseURL);
  selector.type = selector.type || 'TextQuoteSelector';

  switch(selector.type) {
    case 'TextQuoteSelector': default:
      return `${baseURL}#selector(type=${selector.type},prefix=${encodeURIComponent(selector.prefix)},exact=${encodeURIComponent(selector.exact)},suffix=${encodeURIComponent(selector.suffix)})`;
  }
}

export function isDuplicateLocation(annotationDistribution, containerIRI) {
  return Object.keys(annotationDistribution).some(
    item => annotationDistribution[item].containerIRI == containerIRI
  );
}


export function getAnnotationDistribution(action, data) {
  const { id, selectionData, formData } = data;
  let { containerIRI } = data;
  const { selectedParentElement } = selectionData;
  //This annotationInbox is about when the selected text is part of an existing Annotation, it gets that Annotation's own inbox which is used towards announcing the annotation that's about to be created. (This is not related to whether an inbox should be assigned to an annotation that's about to be created.)
  const annotationInbox =  getInboxOfClosestNodeWithSelector(selectedParentElement, '.do[typeof="oa:Annotation"]');
  //These are whether the user wants to send a copy of their annotation to a personal storage and/or to an annotation service.
  const annotationLocationAnnotationStore = formData['annotation-location-annotation-store'];
  const annotationLocationPersonalStorage = formData['annotation-location-personal-storage'];
  const annotationLocationOutbox = formData['annotation-location-activity-outbox'];
  const annotationLocationService = formData['annotation-location-annotation-service'];

  //Use if (activityIndex) when all action values are taken into account e.g., `note` in author mode

  var aLS, noteURL, noteIRI, contextProfile, fromContentType, contentType;
  var annotationDistribution = [];

  let activityTypeMatched = false;
  const activityIndex = Config.ActionActivityIndex[action];

  //XXX: Use TypeIndex location as canonical if available, otherwise storage. Note how noteIRI is treated later
  if (annotationLocationAnnotationStore && Config.User.TypeIndex) {
    // Resolve the registered container from private or public TypeIndex for this action.
    const registeredContainer = getRegisteredAnnotationContainer(action);

    if (registeredContainer) {
      activityTypeMatched = true;

      containerIRI = registeredContainer;

      fromContentType = 'text/html';
      // contentType = 'text/html';
      contentType = fromContentType;

      noteURL = noteIRI = containerIRI + id;
      contextProfile = {
        // 'subjectURI': noteIRI,
      };
      aLS = { 'id': id, 'containerIRI': containerIRI, 'noteURL': noteURL, 'noteIRI': noteIRI, 'fromContentType': fromContentType, 'contentType': contentType, 'canonical': true, 'annotationInbox': annotationInbox };

      annotationDistribution.push(aLS);
    }
  }

  if (annotationLocationOutbox && Config.User.Outbox) {
    containerIRI = Config.User.Outbox[0];

    fromContentType = 'text/html';
    // contentType = 'application/ld+json';
    contentType = fromContentType;

    noteURL = noteIRI = containerIRI + id;
    contextProfile = {
      '@context': [
        'https://www.w3.org/ns/activitystreams',
        { 'oa': 'http://www.w3.org/ns/oa#', 'schema': 'http://schema.org/' }
      ],
      // 'subjectURI': noteIRI,
      'profile': 'https://www.w3.org/ns/activitystreams'
    };
    aLS = { 'id': id, 'containerIRI': containerIRI, 'noteURL': noteURL, 'noteIRI': noteIRI, 'fromContentType': fromContentType, 'contentType': contentType, 'annotationInbox': annotationInbox };
    // Outbox is canonical only when no registered or personal storage copy is selected.
    if (!activityTypeMatched && !annotationLocationPersonalStorage) {
      aLS['canonical'] = true;
    }

    aLS = Object.assign(aLS, contextProfile)

    if (!isDuplicateLocation(annotationDistribution, containerIRI)) {
      annotationDistribution.push(aLS);
    }
  }

  if (annotationLocationPersonalStorage && Config.User.Storage) {
    containerIRI = Config.User.Storage[0];

    fromContentType = 'text/html';
    // contentType = 'text/html';
    contentType = fromContentType;

    noteURL = noteIRI = containerIRI + id;
    contextProfile = {
      // 'subjectURI': noteIRI,
    };
    // The registered (TypeIndex) location, when selected, is the canonical copy.
    aLS = { 'id': id, 'containerIRI': containerIRI, 'noteURL': noteURL, 'noteIRI': noteIRI, 'fromContentType': fromContentType, 'contentType': contentType, 'canonical': !activityTypeMatched, 'annotationInbox': annotationInbox };

    if (!isDuplicateLocation(annotationDistribution, containerIRI)) {
      annotationDistribution.push(aLS);
    }
  }

  if (annotationLocationService && typeof Config.AnnotationService !== 'undefined') {
    containerIRI = Config.AnnotationService;
    fromContentType = 'text/html';
    // contentType = 'application/ld+json';
    contentType = fromContentType;

    contextProfile = {
      '@context': [
        'https://www.w3.org/ns/anno.jsonld',
        { 'as': 'https://www.w3.org/ns/activitystreams#', 'schema': 'http://schema.org/' }
      ],
      // 'subjectURI': noteIRI,
      'profile': 'https://www.w3.org/ns/anno.jsonld'
    };

    if (!annotationLocationAnnotationStore && !annotationLocationPersonalStorage && !annotationLocationOutbox && annotationLocationService) {
      noteURL = noteIRI = containerIRI + id;
      aLS = { 'id': id, 'containerIRI': containerIRI, 'noteURL': noteURL, 'noteIRI': noteIRI, 'fromContentType': fromContentType, 'contentType': contentType, 'canonical': true,'annotationInbox': annotationInbox };
    }
    else if (annotationLocationAnnotationStore || annotationLocationPersonalStorage || annotationLocationOutbox) {
      noteURL = containerIRI + id;
      aLS = { 'id': id, 'containerIRI': containerIRI, 'noteURL': noteURL, 'noteIRI': noteIRI, 'fromContentType': fromContentType, 'contentType': contentType, 'annotationInbox': annotationInbox };
    }
    else {
      noteURL = noteIRI = containerIRI + id;
      aLS = { 'id': id, 'containerIRI': containerIRI, 'noteURL': noteURL, 'noteIRI': noteIRI, 'fromContentType': fromContentType, 'contentType': contentType, 'canonical': true, 'annotationInbox': annotationInbox };
    }

    aLS = Object.assign(aLS, contextProfile)

    if (!isDuplicateLocation(annotationDistribution, containerIRI)) {
      annotationDistribution.push(aLS);
    }
  }

  return annotationDistribution;
}


export function createActivityData(annotation, options = {}) {
  const { id, targetIRI, formData, action } = annotation;

  // console.log(annotation, options)
  var noteIRI = (options.relativeObject) ? '#' + id : annotation['noteIRI'];

  var notificationStatements = '    <dl about="' + noteIRI + '">\n\
<dt>Object type</dt><dd><a about="' + noteIRI + '" typeof="oa:Annotation" href="' + ns.oa.Annotation.value + '">Annotation</a></dd>\n\
<dt>Motivation</dt><dd><a href="' + Config.getPrefixURI(annotation.motivatedBy.split(':')[0]) + annotation.motivatedBy.split(':')[1] + '" property="oa:motivation">' + annotation.motivatedBy.split(':')[1] + '</a></dd>\n\
</dl>\n\
';

  var notificationData = {
    "slug": id,
    "license": formData.license,
    "statements": notificationStatements
  };
// console.log(_this.action)

  if (options.announce) {
    notificationData['type'] = ['as:Announce'];
    notificationData['object'] = noteIRI;
    notificationData['inReplyTo'] = targetIRI;
  }
  else {
    switch(action) {
      default: case 'comment': case 'specificity':
        notificationData['type'] = ['as:Create'];
        notificationData['object'] = noteIRI;
        notificationData['inReplyTo'] = targetIRI;
        break;
      case 'approve':
        notificationData['type'] = ['as:Like'];
        notificationData['object'] = targetIRI;
        notificationData['context'] = noteIRI;
        break;
      case 'disapprove':
        notificationData['type'] = ['as:Dislike'];
        notificationData['object'] = targetIRI;
        notificationData['context'] = noteIRI;
        break;
      case 'bookmark':
        notificationData['type'] = ['as:Add'];
        notificationData['object'] = noteIRI;
        notificationData['target'] = annotation['containerIRI'];
        break;
    }
  }

// console.log(notificationData);
  return notificationData;
}



export function positionActivity(annotation, options) {
  if (!annotation['canonical']) {
    return Promise.resolve();
  }

  if ('profile' in annotation && annotation.profile == 'https://www.w3.org/ns/activitystreams') {
    return showActivities(annotation['noteIRI'])
      .catch((error) => {
        console.log('Error showing activities:', error)
        return Promise.resolve()
      })
  }
  else {
// console.log(options)
    return showActivities(annotation[ 'noteIRI' ], options)
      .catch((error) => {
        console.log('Error showing activities:', error)
        return Promise.resolve()
      })
  }
}



function sendNotification(annotation, options) {
  const documentURL = Config.DocumentURL;

  if (!annotation['canonical']) {
    return Promise.resolve();
  }

  var inboxPromise;

  if (annotation.annotationInbox) {
    inboxPromise = Promise.resolve([annotation.annotationInbox])
  }
  else {
    if ('inbox' in Config.Resource[documentURL] && Config.Resource[documentURL].inbox.length) {
      inboxPromise = Promise.resolve(Config.Resource[documentURL].inbox)
    }
    else {
      inboxPromise =
        getLinkRelation(ns.ldp.inbox.value, documentURL)
          .catch(() => {
            return getLinkRelationFromRDF(ns.as.inbox.value, documentURL);
          });
    }
  }

  return inboxPromise
    .catch(error => {
      // console.log('Error fetching ldp:inbox and as:inbox endpoint:', error)
      throw error
    })
    .then(inboxes => {
      // TODO: resourceIRI for getLinkRelation should be the
      // closest IRI (not necessarily the document).
// console.log(inboxes)
      if (inboxes.length) {
        var notificationData = createActivityData(annotation, { 'announce': true });

        notificationData['inbox'] = inboxes[0];

        // notificationData['type'] = ['as:Announce'];
// console.log(annotation)
// console.log(notificationData)
        return notifyInbox(notificationData)
          .catch(error => {
            console.log('Error notifying the inbox:', error)
          })
      }
    })
}
